import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useResource, useInvalidator } from 'rest-hooks';
import { colors } from '@contently/toolkit/dist/styles/theme';
import { ReplyChain, CommentReply, H6, H5 } from '@contently/toolkit';
import CommentResource from '../../resources/CommentResource';
import { updateCommentListCacheBust } from '../../store/textEditorReducer';

const ReplyWrapper = styled('div')`
  padding-left: 20px;
  padding-top: 15px;
  display: flex;
  line-height: 16px;
  color: #666;
  background-image: linear-gradient(to right, ${colors.grey.secondaryDark} 33%, transparent 0%);
  background-position: top;
  background-size: 2px 1px;
  background-repeat: repeat-x;
`;

const StyledH5 = styled(H5)`
    font-weight: 400;
    cursor: pointer;
    box-sizing: border-box;
    font-size: 12px;
`;

const DotIcon = styled.span`
  &:before {
    content: "•";
    display: inline-block;
    margin: 0 8px;
    pointer-events: none;
  }
`;

function DefaultReplyView({ numReplies, reply }) {
  if (!reply) return null;
  const numRepliesCopy = numReplies > 1 ? `${numReplies} Replies` : `${numReplies} Reply`;

  return (
    <React.Fragment>
      <ReplyWrapper>
        <H6>{numRepliesCopy}</H6><DotIcon /><StyledH5>most recent reply</StyledH5>
      </ReplyWrapper>
      <CommentReply {...reply} defaultView />
    </React.Fragment>
  );
}

const replyTypes = {
  body: PropTypes.string,
  avatar: PropTypes.string.isRequired,
  fullName: PropTypes.string.isRequired,
  formattedCreatedAt: PropTypes.string.isRequired,
  authorIsEmployee: PropTypes.bool,
  authorIsManagingEditor: PropTypes.bool
};

DefaultReplyView.propTypes = {
  numReplies: PropTypes.number.isRequired,
  reply: PropTypes.shape(replyTypes).isRequired
};

export default function ReplyChainContainer({ commentId, showAllComments, sidebar }) {
  const dispatch = useDispatch();
  const invalidateCommentListCache = useInvalidator(CommentResource.listShape());
  const { commentListCacheBust } = useSelector(state => state.textEditor);
  const replies = useResource(CommentResource.listShape(), {
    parent_id: commentId
  });

  const withAvatars = replies.map(reply => {
    const { fullName, avatar } = reply.authorable;
    return { ...reply, fullName, avatar };
  });

  const lastReply = replies.length ? withAvatars[replies.length - 1] : null;

  useEffect(() => {
    if (commentListCacheBust && commentListCacheBust === commentId && sidebar) {
      invalidateCommentListCache({ parent_id: commentId });
      dispatch(updateCommentListCacheBust(null));
    }
  }, [commentId, commentListCacheBust, dispatch, invalidateCommentListCache, sidebar]);

  return showAllComments ? (
    <ReplyChain replies={withAvatars} />
  ) : (
    lastReply && <DefaultReplyView reply={lastReply} numReplies={replies.length} />
  );
}

ReplyChainContainer.defaultProps = {
  sidebar: false
};

ReplyChainContainer.propTypes = {
  commentId: PropTypes.string.isRequired,
  showAllComments: PropTypes.bool.isRequired,
  sidebar: PropTypes.bool
};



// WEBPACK FOOTER //
// ./app/javascript/client/components/textEditor/ReplyChainContainer.jsx